import { Command as CommandPrimitive } from "cmdk";
import { Check, Loader2, X } from "lucide-react";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useTransition,
  useDeferredValue,
} from "react";

import {
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "#app/components/ui/command.tsx";
import { Skeleton } from "#app/components/ui/skeleton.tsx";
import { cn } from "#app/utils/misc.tsx";

type Option = {
  label: string;
  value: string;
  placeId: string;
};

type AutoCompleteProps = {
  options: Option[];
  emptyMessage: string;
  value?: Option;
  onValueChange?: (value: Option | undefined) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  query?: string;
  setQuery: (query: string) => void;
  clearable?: boolean;
  required?: boolean;
  errorMessage?: string;
  noOptionsMessage?: string;
  maxSuggestions?: number;
  aria?: {
    label?: string;
    description?: string;
  };
};

export const AutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isLoading = false,
  query,
  setQuery,
  clearable = true,
  required = false,
  errorMessage,
  noOptionsMessage = "Nenhum resultado encontrado.",
  maxSuggestions = 5,
  aria,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option | undefined>(value);
  const initialQueryRef = useRef(true);
  const [localQuery, setLocalQuery] = useState(query || "");
  const deferredLocalQuery = useDeferredValue(localQuery);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const [isTouched, setIsTouched] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const justSelectedRef = useRef(false);
  const typingRef = useRef(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [isPending, startTransition] = useTransition();
  const uniqueId = useId();
  const listboxId = `autocomplete-listbox-${uniqueId}`;
  const inputId = `autocomplete-input-${uniqueId}`;
  const descriptionId = `autocomplete-description-${uniqueId}`;
  const errorId = `autocomplete-error-${uniqueId}`;

  // Limit displayed options to improve performance
  const displayedOptions = options.slice(0, maxSuggestions);
  const hasMoreOptions = options.length > maxSuggestions;

  // Calculate combined immediate processing state
  const isProcessing = isLoading || isPending || isDebouncing;
  // Defer the processing state used for list content rendering
  const deferredIsProcessing = useDeferredValue(isProcessing);

  // Function to highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query || typeof text !== "string") return text; // Add type check for safety
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    const before = text.substring(0, index);
    const match = text.substring(index, index + query.length);
    const after = text.substring(index + query.length);
    // Return an array instead of a fragment to be explicit about sequential rendering
    return [
      before,
      <strong key="match" className="font-bold text-primary">
        {match}
      </strong>,
      after,
    ];
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // For initial sync with external query
  useEffect(() => {
    if (initialQueryRef.current && query && query.length > 0) {
      setLocalQuery(query);
      initialQueryRef.current = false;
    }
  }, [query]);

  // Sync with external value changes
  useEffect(() => {
    setSelected(value);
    // If we receive a new value, update the local query too
    if (value?.label) {
      setLocalQuery(value.label);
    }
  }, [value]);

  const resetSearch = useCallback(() => {
    setLocalQuery("");
    setSelected(undefined);
    onValueChange?.(undefined);
    setIsDebouncing(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setOpen(false); // Close dropdown on reset

    startTransition(() => {
      setQuery("");
    });

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onValueChange, setQuery]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) return;

      if (event.key === "Enter" && input.value !== "" && isOpen) {
        event.preventDefault();
        const optionToSelect = options.find(
          (option) =>
            option.label === input.value || option.label === options[0]?.label,
        );
        if (optionToSelect) {
          setSelected(optionToSelect);
          onValueChange?.(optionToSelect);
          setLocalQuery(optionToSelect.label);
          setIsDebouncing(false);

          startTransition(() => {
            setQuery(optionToSelect.label);
          });

          setOpen(false); // Close on selection
          justSelectedRef.current = true;
        }
      }

      if (event.key === "Escape") {
        setOpen(false); // Close on Escape
        if (!isOpen && clearable && !required && localQuery) {
          resetSearch();
        }
        input.blur();
      }

      if (
        event.key === "Backspace" &&
        input.value === "" &&
        selected &&
        clearable &&
        !required
      ) {
        resetSearch();
      }

      if (event.key === "ArrowDown" && !isOpen && localQuery) {
        event.preventDefault();
        setOpen(true); // Open on ArrowDown if closed with query
      }
    },
    [
      options,
      onValueChange,
      setQuery,
      isOpen,
      selected,
      clearable,
      required,
      localQuery,
      resetSearch,
    ],
  );

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setOpen(false); // Close on selection
      setLocalQuery(selectedOption.label);
      setSelected(selectedOption);
      onValueChange?.(selectedOption);
      setIsDebouncing(false);

      startTransition(() => {
        setQuery(selectedOption.label);
      });

      setHasInteracted(true);
      justSelectedRef.current = true;
    },
    [onValueChange, setQuery],
  );

  const handleInputChange = useCallback(
    (newValue: string) => {
      typingRef.current = true;
      setLocalQuery(newValue);
      setHasInteracted(true);
      justSelectedRef.current = false;

      if (newValue.length > 0) {
        setOpen(true); // Open dropdown when typing starts
        setIsDebouncing(true);

        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
          startTransition(() => {
            setQuery(newValue);
            setIsDebouncing(false);
          });
          typingRef.current = false;
        }, 300);
      } else {
        setOpen(false); // Close dropdown if input becomes empty
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
        setIsDebouncing(false);
        typingRef.current = false;

        requestAnimationFrame(() => {
          startTransition(() => {
            setQuery("");
            if (selected && !required) {
              setSelected(undefined);
              onValueChange?.(undefined);
            }
          });
        });
      }
    },
    [setQuery, selected, onValueChange, required],
  );

  const handleFocus = useCallback(() => {
    setHasFocus(true);
    setIsTouched(true);

    // Open dropdown on focus only if there is text already
    if (
      localQuery &&
      localQuery.length > 0 &&
      !justSelectedRef.current &&
      !typingRef.current
    ) {
      requestAnimationFrame(() => {
        if (inputRef.current?.matches(":focus")) {
          setOpen(true);
        }
      });
    }
    requestAnimationFrame(() => {
      justSelectedRef.current = false;
    });
  }, [localQuery]);

  const handleBlur = useCallback(() => {
    setHasFocus(false);
    setTimeout(() => {
      if (!inputRef.current?.matches(":focus")) {
        setOpen(false); // Close dropdown on blur
      }
      if (localQuery && !selected && required) {
        if (value) {
          requestAnimationFrame(() => {
            setLocalQuery(value.label);
            setSelected(value);
          });
        }
      }
    }, 100); // Keep delay
  }, [localQuery, selected, value, required]);

  // Determine if we should show error state
  const showError =
    isTouched && hasInteracted && required && !selected && errorMessage;

  // Memoize the rendering of list items to optimize performance
  const memoizedListItems = useMemo(
    () =>
      displayedOptions.map((option) => {
        const isSelected = selected?.placeId === option.placeId;
        return (
          <CommandItem
            key={option.placeId}
            value={option.label}
            id={option.placeId}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onSelect={() => handleSelectOption(option)}
            className={cn(
              "px-0 py-0",
              "data-[selected]:bg-transparent",
              "aria-selected:bg-transparent",
            )}
            aria-selected={isSelected}
            role="option"
          >
            <div
              className={cn(
                "flex min-h-10 w-full flex-col items-start rounded-lg",
                "my-0.5 bg-background px-2 py-1 hover:bg-muted-foreground/10",
                isSelected ? "bg-muted-foreground/10" : "",
                !isSelected ? "pl-2" : "",
              )}
            >
              <div className="flex w-full items-center gap-1">
                {isSelected ? (
                  <Check className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                ) : null}
                <span className="truncate">
                  {highlightMatch(option.label, deferredLocalQuery)}{" "}
                  {/* Keep deferred for highlight if needed */}
                </span>
              </div>
              <span className="max-w-full truncate text-muted-foreground text-xs ml-5">
                {option.value}
              </span>
            </div>
          </CommandItem>
        );
      }),
    [
      displayedOptions,
      selected,
      deferredLocalQuery,
      handleSelectOption,
      highlightMatch,
    ], // Keep deferredLocalQuery dependency
  );

  return (
    <div className="relative">
      <CommandPrimitive
        shouldFilter={false}
        onKeyDown={handleKeyDown}
        className="relative"
        loop={true}
      >
        <div
          className={cn(
            "relative flex items-center border-b",
            showError ? "border-red-500" : "border-input",
          )}
        >
          <CommandInput
            ref={inputRef}
            id={inputId}
            value={localQuery}
            onValueChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              "text-sm w-full pr-9 border-0 rounded-none ring-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
              disabled ? "opacity-60 cursor-not-allowed" : "",
              (isPending || isDebouncing) && !isLoading ? "caret-primary" : "",
            )}
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={selected?.placeId}
            aria-invalid={showError ? "true" : "false"}
            aria-label={aria?.label}
            aria-describedby={cn(
              aria?.description ? descriptionId : undefined,
              showError ? errorId : undefined,
            )}
            role="combobox"
            spellCheck="false"
            autoComplete="off"
          />
          {isLoading && (
            <div className="absolute right-10 flex items-center justify-center">
              <Loader2
                className="h-4 w-4 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
            </div>
          )}
          {(isPending || isDebouncing) && !isLoading && (
            <div className="absolute right-10 flex items-center justify-center">
              {/* Use a different style/icon for pending/debouncing vs initial loading if desired */}
              <Loader2
                className="h-4 w-4 animate-spin text-muted-foreground opacity-50"
                aria-hidden="true"
              />
            </div>
          )}
          {clearable && localQuery && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resetSearch();
              }}
              className="absolute right-2 flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Clear input"
              tabIndex={-1}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="relative mt-1">
          <div
            className={cn(
              "absolute top-0 z-10 w-full rounded-xl bg-background outline-none shadow-md overflow-hidden",
              "transition-opacity duration-150 ease-out",
              isOpen
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none",
            )}
          >
            <CommandList
              id={listboxId}
              className="rounded-lg ring-1 ring-slate-200 max-h-[300px] overflow-y-auto"
            >
              {deferredIsProcessing ? (
                <CommandPrimitive.Loading>
                  <div className="p-2 space-y-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex flex-col gap-1 p-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    ))}
                  </div>
                </CommandPrimitive.Loading>
              ) : displayedOptions.length > 0 ? (
                <CommandGroup>
                  {memoizedListItems}
                  {hasMoreOptions && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground border-t">
                      + {options.length - maxSuggestions} mais opções
                      disponíveis. Continue digitando para refinar resultados.
                    </div>
                  )}
                </CommandGroup>
              ) : (
                <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                  {`${emptyMessage || noOptionsMessage}${localQuery ? `: "${localQuery}"` : ""}`}
                  {localQuery && options.length === 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Verifique a ortografia ou tente termos diferentes.
                    </div>
                  )}
                </CommandPrimitive.Empty>
              )}
            </CommandList>
          </div>
        </div>
      </CommandPrimitive>

      {aria?.description && (
        <span id={descriptionId} className="sr-only">
          {aria.description}
        </span>
      )}

      {showError && (
        <div id={errorId} className="text-red-500 text-xs mt-1">
          {errorMessage}
        </div>
      )}
    </div>
  );
};
